import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  BookOpenIcon, 
  ClockIcon,
  DocumentTextIcon,
  VideoCameraIcon,
  CodeBracketIcon,
  CheckCircleIcon,
  PlayIcon,
  LockClosedIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  AcademicCapIcon,
  UsersIcon,
  TrophyIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { Navbar, Footer } from './ui';
import apiService from '../services/api';

const PublicCourseDetails = ({ isAuthenticated }) => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [expandedModule, setExpandedModule] = useState(0);
  const [expandedFaq, setExpandedFaq] = useState(-1);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load course data from API
  useEffect(() => {
    const loadCourseData = async () => {
      try {
        setLoading(true);
        setError(null);

        // First, check if courseId is numeric (direct ID lookup)
        let courseData;
        const isNumericId = /^\d+$/.test(courseId);
        
        if (isNumericId) {
          // Try direct ID lookup
          try {
            courseData = await apiService.getCourseDetails(parseInt(courseId));
          } catch (err) {
            console.error('Failed to fetch course by ID:', err);
            throw new Error('Course not found');
          }
        } else {
          // Course ID is a slug (e.g., "forex-trading"), find by slug
          const allCourses = await apiService.getCourses();
          
          if (!allCourses || allCourses.length === 0) {
            throw new Error('No courses available');
          }
          
          // Try to find course by slug match
          const foundCourse = allCourses.find(c => {
            if (!c || !c.name) return false;
            const courseSlug = c.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
            const matchSlug = courseId.toLowerCase();
            return courseSlug === matchSlug;
          });
          
          if (foundCourse && foundCourse.id) {
            try {
              courseData = await apiService.getCourseDetails(foundCourse.id);
            } catch (err) {
              console.error('Failed to fetch course details after slug match:', err);
              throw new Error('Failed to load course details');
            }
          } else {
            console.error('Course not found by slug:', courseId);
            console.log('Available courses:', allCourses.map(c => ({ id: c.id, name: c.name })));
            throw new Error('Course not found');
          }
        }
        
        // Validate that we have essential course data
        if (!courseData || !courseData.id) {
          throw new Error('Invalid course data received');
        }

        // Load course modules
        let modulesData = [];
        if (courseData && courseData.id) {
          try {
            modulesData = await apiService.getCourseModules(courseData.id);
          } catch (err) {
            console.warn('Failed to load modules:', err);
          }
        }

        // Transform course data to match frontend format
        const getCurrencySymbol = (currency) => {
          switch ((currency || '').toUpperCase()) {
            case 'NGN': return '₦';
            case 'USD': return '$';
            case 'GBP': return '£';
            case 'EUR': return '€';
            default: return '';
          }
        };
        const formattedPrice = (courseData.cost_amount && courseData.cost_currency)
          ? `${getCurrencySymbol(courseData.cost_currency)}${Number(courseData.cost_amount).toLocaleString()}`
          : null;
        // Ensure courseData has required fields with fallbacks
        const courseName = courseData.name || courseData.title || 'Course';
        const courseDescription = courseData.description || 'This comprehensive course will help you master new skills.';
        const durationMonths = courseData.duration_months || 3; // Default to 3 months if not specified
        
        const transformedCourse = {
          id: courseData.id,
          title: courseName,
          subtitle: courseDescription.substring(0, 100) + (courseDescription.length > 100 ? '...' : ''),
          description: courseDescription,
          writeUp: courseData.write_up || `Welcome to ${courseName}!\n\nThis comprehensive course will take you from beginner to advanced. Here are some important points to remember:\n\n📚 Course Structure:\n• Structured learning modules\n• Hands-on practical exercises\n• Real-world projects\n• Industry best practices\n\n💡 Tips for Success:\n• Practice regularly\n• Build projects alongside lessons\n• Join our community discussions\n• Don't hesitate to ask questions\n\n🛠️ Required Tools:\n• Computer with internet connection\n• Dedication and commitment\n• Willingness to learn\n\nRemember: The key to mastering any skill is consistent practice and building real projects!`,
          pdfFile: courseData.pdf_file || null,
          instructor: courseData.instructor_name ? {
            name: courseData.instructor_name,
            title: "Expert Instructor",
            experience: "5+ years",
            students: "1000+",
            bio: "Experienced instructor with expertise in this field."
          } : null,
          features: {
            level: "Beginner to Advanced",
            duration: `${durationMonths} months`,
            lessons: `${modulesData.length * 4} lessons`,
            projects: `${Math.floor(modulesData.length / 2)} projects`,
            hours: `${durationMonths * 20}+ hours`
          },
          stats: {
            rating: courseData.rating || 0,
            reviews: courseData.reviews || 0,
            students: courseData.total_students_enrolled || 0,
            completion: "94%"
          },
          price: {
            amount: formattedPrice || 'Contact for price',
            currency: courseData.cost_currency || '',
            discount: ""
          },
          skills: [
            "Core concepts and fundamentals",
            "Practical applications",
            "Best practices",
            "Real-world examples",
            "Hands-on projects",
            "Industry standards"
          ],
          modules: modulesData.map((module, index) => ({
            id: module.id,
            title: module.name,
            duration: `${Math.ceil(module.duration_hours / 8)} weeks`,
            description: module.description || `Module ${index + 1}: ${module.name}`,
            lessons: [
              { id: module.id * 10 + 1, title: "Introduction", type: "video", duration: "45 min", preview: true },
              { id: module.id * 10 + 2, title: "Core Concepts", type: "video", duration: "60 min", preview: false },
              { id: module.id * 10 + 3, title: "Practical Exercise", type: "interactive", duration: "90 min", preview: false },
              { id: module.id * 10 + 4, title: "Project Work", type: "project", duration: "120 min", preview: false }
            ]
          })),
          prerequisites: [
            "Basic computer literacy",
            "High school mathematics",
            "Willingness to learn and practice"
          ],
          outcomes: [
            "Master core concepts and techniques",
            "Build practical projects",
            "Apply knowledge in real-world scenarios",
            "Develop problem-solving skills",
            "Gain industry-recognized skills",
            "Prepare for career advancement"
          ],
          faq: [
            {
              question: "Do I need prior experience?",
              answer: "No! This course starts from the absolute basics and gradually builds up your skills."
            },
            {
              question: "What tools will I need?",
              answer: "All you need is a computer with internet access. We'll guide you through installing any required software."
            },
            {
              question: "How much time should I dedicate?",
              answer: "We recommend 8-10 hours per week for optimal learning. The course is self-paced."
            },
            {
              question: "Will I get a certificate?",
              answer: "Yes! You'll receive a certificate of completion for your portfolio."
            }
          ]
        };

        setCourse(transformedCourse);
        setModules(modulesData);

      } catch (err) {
        console.error('Failed to load course data:', err);
        setError('Failed to load course details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (courseId) {
      loadCourseData();
    }
  }, [courseId]);

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar links={[]} />
        <div className="pt-24 pb-12 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading course details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error || !course) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar links={[]} />
        <div className="pt-24 pb-12 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto px-4">
            <div className="text-red-600 text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Course Not Found</h2>
            <p className="text-gray-600 mb-6">{error || 'This course could not be loaded.'}</p>
            <button 
              onClick={() => navigate('/courses')} 
              className="btn-primary"
            >
              Back to Courses
            </button>
          </div>
        </div>
      </div>
    );
  }

  const getLessonIcon = (type) => {
    switch (type) {
      case 'video': return VideoCameraIcon;
      case 'interactive': return CodeBracketIcon;
      case 'reading': return DocumentTextIcon;
      case 'project': return TrophyIcon;
      default: return BookOpenIcon;
    }
  };

  const handleLessonClick = (lesson) => {
    if (lesson.preview) {
      // Show preview for free lessons
      setShowEnrollModal(true);
    } else if (isAuthenticated) {
      // Navigate to course learning page
      navigate(`/learn/${courseId}`);
    } else {
      // Show enrollment modal
      setShowEnrollModal(true);
    }
  };

  const handleEnrollClick = () => {
    // Navigate to enrollment page for proper enrollment flow
    navigate(`/enroll/${courseId}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900 pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center">
            {/* Course Info */}
            <div className="lg:col-span-2 text-white">
              <div className="mb-6">
                <span className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium">
                  {course.features.level}
                </span>
              </div>
              
              <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                {course.title}
              </h1>
              
              <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                {course.subtitle}
              </p>

              {/* Course Stats */}
              <div className="flex flex-wrap gap-6 mb-8">
                <div className="flex items-center text-blue-100">
                  <div className="flex items-center mr-2">
                    {[...Array(5)].map((_, i) => (
                      <StarIconSolid key={i} className="w-5 h-5 text-yellow-400" />
                    ))}
                  </div>
                  <span className="font-bold text-white">{course.stats.rating}</span>
                  <span className="ml-1">({course.stats.reviews.toLocaleString()} reviews)</span>
                </div>
                <div className="flex items-center text-blue-100">
                  <UsersIcon className="w-5 h-5 mr-2" />
                  <span>{course.stats.students.toLocaleString()} students</span>
                </div>
                <div className="flex items-center text-blue-100">
                  <ClockIcon className="w-5 h-5 mr-2" />
                  <span>{course.features.hours}</span>
                </div>
              </div>

              {/* Instructor */}
              {course.instructor && (
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white font-bold">
                      {course.instructor.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">{course.instructor.name}</h3>
                    <p className="text-blue-100 text-sm">{course.instructor.title}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Enrollment Card */}
            <div className="lg:col-span-1">
              <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-white/20">
                <div className="text-center mb-6">
                  <div className="text-4xl font-bold text-gray-900 mb-2">{course.price.amount}</div>
                  <div className="text-green-600 font-medium">{course.price.discount}</div>
                  <div className="text-gray-600">One-time payment</div>
                </div>

                <button 
                  onClick={handleEnrollClick}
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold py-4 px-6 rounded-2xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 mb-4 text-lg transform hover:scale-[1.02] shadow-lg hover:shadow-xl"
                >
                  {isAuthenticated ? 'Enroll Now' : 'Login to Enroll'}
                </button>

                <div className="text-center mb-6">
                  <p className="text-sm text-gray-600">30-day money-back guarantee</p>
                </div>

                <div className="space-y-3 text-sm">
                  <div className="flex items-center space-x-3">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>Lifetime access to course content</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>{course.features.projects} hands-on projects</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>Certificate of completion</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>Job placement assistance</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left Column - Course Content */}
          <div className="lg:col-span-2 space-y-16">
            {/* About the Course */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">About the Course</h2>
              <div className="prose prose-lg max-w-none text-gray-700 mb-8">
                {course.description}
              </div>
              <p className="text-gray-600 mb-6">
                This course is designed for complete beginners who want to break into tech,
                career switchers looking to upskill, and professionals who want to add 
                data science to their toolkit. No prior experience required!
              </p>

              {/* Course Write-up */}
              {course.writeUp && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 mb-8 border border-blue-100">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <BookOpenIcon className="w-5 h-5 mr-2 text-blue-600" />
                    Course Information & Resources
                  </h3>
                  <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-line">
                    {course.writeUp}
                  </div>
                  
                  {/* PDF Download */}
                  {course.pdfFile && (
                    <div className="mt-6 p-4 bg-white rounded-xl border border-blue-200 flex items-center justify-between">
                      <div className="flex items-center">
                        <DocumentTextIcon className="w-6 h-6 text-red-600 mr-3" />
                        <div>
                          <h4 className="font-semibold text-gray-900">Course Syllabus & Resources</h4>
                          <p className="text-sm text-gray-600">Download detailed course outline and additional materials</p>
                        </div>
                      </div>
                      <a 
                        href={course.pdfFile} 
                        download
                        className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center"
                      >
                        <DocumentTextIcon className="w-4 h-4 mr-2" />
                        Download PDF
                      </a>
                    </div>
                  )}
                </div>
              )}

              <h3 className="text-xl font-bold text-gray-900 mb-4">What You'll Learn</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                {course.skills.map((skill, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{skill}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Course Curriculum */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Course Content</h2>
              <p className="text-gray-600 mb-8">
                {course.features.duration} • {course.modules.length} Modules • {course.features.lessons}
              </p>
              
              <div className="space-y-4">
                {course.modules.map((module, index) => (
                  <div key={module.id || `module-${index}`} className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-3xl overflow-hidden shadow-lg">
                    <button
                      onClick={() => setExpandedModule(expandedModule === index ? -1 : index)}
                      className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-gray-50/80 transition-all duration-300"
                    >
                      <div>
                        <h3 className="font-bold text-gray-900 text-lg">
                          Module {index + 1}: {module.title}
                        </h3>
                        <p className="text-gray-600 mt-1">
                          {module.duration} • {module.lessons.length} lessons
                        </p>
                        <p className="text-gray-500 text-sm mt-2">
                          {module.description}
                        </p>
                      </div>
                      {expandedModule === index ? (
                        <ChevronUpIcon className="w-6 h-6 text-gray-400 flex-shrink-0 ml-4 transform transition-transform duration-300" />
                      ) : (
                        <ChevronDownIcon className="w-6 h-6 text-gray-400 flex-shrink-0 ml-4 transform transition-transform duration-300" />
                      )}
                    </button>
                    
                    {expandedModule === index && (
                      <div className="px-8 pb-6 bg-gradient-to-br from-gray-50/80 to-white/60 backdrop-blur-sm">
                        <div className="space-y-3">
                          {module.lessons.map((lesson, lessonIndex) => {
                            const Icon = getLessonIcon(lesson.type);
                            return (
                              <div
                                key={lesson.id || `lesson-${lessonIndex}`}
                                onClick={() => handleLessonClick(lesson)}
                                className={`flex items-center justify-between p-4 bg-white/80 backdrop-blur-sm rounded-2xl border cursor-pointer hover:border-indigo-300 hover:shadow-md transition-all duration-300 transform hover:scale-[1.01] ${
                                  lesson.preview ? 'border-green-200 bg-green-50/80' : 'border-gray-200/60'
                                }`}
                              >
                                <div className="flex items-center space-x-3">
                                  <Icon className={`w-5 h-5 ${lesson.preview ? 'text-green-600' : 'text-gray-400'}`} />
                                  <div>
                                    <h4 className="font-medium text-gray-900">{lesson.title}</h4>
                                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                                      <ClockIcon className="w-4 h-4" />
                                      <span>{lesson.duration}</span>
                                      {lesson.preview && (
                                        <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium">
                                          Preview
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                {lesson.preview ? (
                                  <PlayIcon className="w-5 h-5 text-green-600" />
                                ) : (
                                  <LockClosedIcon className="w-5 h-5 text-gray-400" />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* Learning Outcomes */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Learning Outcomes</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {course.outcomes.map((outcome, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <TrophyIcon className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{outcome}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQ */}
            <section>
              <div className="bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900 rounded-3xl p-12 mb-8 relative overflow-hidden">
                {/* Background decoration */}
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/20 to-purple-600/20 backdrop-blur-sm"></div>
                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-white/10 to-transparent rounded-full -translate-y-32 translate-x-32"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-blue-500/20 to-transparent rounded-full translate-y-24 -translate-x-24"></div>
                
                <div className="relative z-10 text-center">
                  <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
                    Got Questions?
                  </h2>
                  <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                    We've got answers! Here are the most common questions about this course.
                  </p>
                  <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-purple-400 mx-auto rounded-full"></div>
                </div>
              </div>

              <div className="space-y-4">
                {course.faq.map((item, index) => (
                  <div key={index} className="bg-white/90 backdrop-blur-xl border border-white/30 rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300">
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === index ? -1 : index)}
                      className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-gradient-to-r hover:from-indigo-50/50 hover:to-purple-50/50 transition-all duration-300 group"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                          <span className="text-white font-bold text-lg">Q</span>
                        </div>
                        <h3 className="font-bold text-gray-900 text-lg group-hover:text-indigo-700 transition-colors duration-300">
                          {item.question}
                        </h3>
                      </div>
                      <div className="flex-shrink-0 ml-4">
                        {expandedFaq === index ? (
                          <ChevronUpIcon className="w-6 h-6 text-indigo-600 transform transition-all duration-300 group-hover:scale-110" />
                        ) : (
                          <ChevronDownIcon className="w-6 h-6 text-gray-400 transform transition-all duration-300 group-hover:text-indigo-600 group-hover:scale-110" />
                        )}
                      </div>
                    </button>
                    
                    {expandedFaq === index && (
                      <div className="px-8 pb-8 bg-gradient-to-br from-gray-50/80 to-indigo-50/40 backdrop-blur-sm animate-in slide-in-from-top-2 duration-300">
                        <div className="flex items-start space-x-4">
                          <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-teal-500 rounded-2xl flex items-center justify-center flex-shrink-0 mt-2">
                            <span className="text-white font-bold text-lg">A</span>
                          </div>
                          <div className="flex-1 pt-3">
                            <p className="text-gray-700 leading-relaxed text-lg">
                              {item.answer}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Additional Help Section */}
              <div className="mt-12 text-center">
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-3xl p-8 border border-indigo-100">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Still have questions?</h3>
                  <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                    Can't find what you're looking for? Our support team is here to help you make the right decision.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <button className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold py-3 px-8 rounded-2xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-[1.02] shadow-lg hover:shadow-xl">
                      Contact Support
                    </button>
                    <button className="border-2 border-indigo-200 text-indigo-700 font-bold py-3 px-8 rounded-2xl hover:bg-indigo-50 hover:border-indigo-300 transition-all duration-300 transform hover:scale-[1.02]">
                      Schedule a Call
                    </button>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Right Column - Course Features */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 space-y-8">
              {/* Course Features */}
              <div className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-3xl shadow-xl p-8">
                <h3 className="text-lg font-bold text-gray-900 mb-6">Course Features</h3>
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl flex items-center justify-center">
                      <AcademicCapIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{course.features.level}</div>
                      <div className="text-sm text-gray-500">Difficulty Level</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center">
                      <ClockIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{course.features.duration}</div>
                      <div className="text-sm text-gray-500">Course Duration</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-teal-500 rounded-2xl flex items-center justify-center">
                      <DocumentTextIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{course.features.lessons}</div>
                      <div className="text-sm text-gray-500">Total Lessons</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-2xl flex items-center justify-center">
                      <TrophyIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{course.features.projects}</div>
                      <div className="text-sm text-gray-500">Hands-on Projects</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Prerequisites */}
              <div className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-3xl shadow-xl p-8">
                <h3 className="text-lg font-bold text-gray-900 mb-6">Prerequisites</h3>
                <div className="space-y-3">
                  {course.prerequisites.map((prereq, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 text-sm">{prereq}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Instructor */}
              {course.instructor && (
                <div className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-3xl shadow-xl p-8">
                  <h3 className="text-lg font-bold text-gray-900 mb-6">Your Instructor</h3>
                  <div className="text-center">
                    <div className="w-20 h-20 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <span className="text-white font-bold text-2xl">
                        {course.instructor.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <h4 className="font-bold text-gray-900">{course.instructor.name}</h4>
                    <p className="text-gray-600 text-sm mb-3">{course.instructor.title}</p>
                    <div className="flex justify-center space-x-4 text-sm text-gray-500 mb-4">
                      <div>{course.instructor.experience} experience</div>
                      <div>{course.instructor.students} students</div>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">{course.instructor.bio}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Enrollment Modal */}
      {showEnrollModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl max-w-md w-full p-8 border border-white/20 shadow-2xl">
            <div className="text-center">
              <LockClosedIcon className="w-16 h-16 text-indigo-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {isAuthenticated ? 'Enroll to Continue' : 'Login Required'}
              </h3>
              <p className="text-gray-600 mb-6">
                {isAuthenticated 
                  ? 'Enroll in this course to access all lessons and materials.'
                  : 'Please log in or sign up to access course materials and track your progress.'
                }
              </p>
              <div className="space-y-3">
                <button 
                  onClick={handleEnrollClick}
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold py-3 px-6 rounded-2xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-[1.02]"
                >
                  {isAuthenticated ? 'Enroll Now' : 'Login to Enroll'}
                </button>
                <button 
                  onClick={() => setShowEnrollModal(false)}
                  className="w-full border border-gray-300 text-gray-700 font-medium py-3 px-6 rounded-2xl hover:bg-gray-50 transition-colors"
                >
                  Continue Browsing
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default PublicCourseDetails; 